import React, { useState } from "react";
import * as Styled from "./styles";
import {
  odontogramaInferior,
  odontogramaSuperior,
  odontogramaDeciduoSuperior,
  odontogramaDeciduoInferior,
} from "shared/data/odontograma";
import { useClinicalBudget } from "shared/providers/ClinicalBudget";
import { IoClose, IoChevronDown, IoChevronUp } from "react-icons/io5";

const permanentTeethIds = [
  ...odontogramaSuperior.map(t => t.id),
  ...odontogramaInferior.map(t => t.id),
];

const deciduousTeethIds = [
  ...odontogramaDeciduoSuperior.map(t => t.id),
  ...odontogramaDeciduoInferior.map(t => t.id),
];

const isDeciduousTooth = (id: number): boolean => deciduousTeethIds.includes(id);
const isPermanentTooth = (id: number): boolean => permanentTeethIds.includes(id);

const BudgetOdontograma: React.FC = () => {
  const {
    showOdontograma,
    setShowOdontograma,
    selectedTeeth,
    setSelectedTeeth,
    selectedProc,
    selectedArea,
    selectedCategoria,
    selectedValor,
    selectedCatprocId,
    addItem,
    setSelectedProc,
    setSelectedCategoria,
    setSelectedGrupo,
    facesPorElemento,
    setFacesPorElemento,
    setShowFacesModal,
    setPendingToothForFaces,
    effectiveQtdFaces,
    formatMoeda,
    showValues,
  } = useClinicalBudget();

  const [showDeciduos, setShowDeciduos] = useState(false);

  if (!showOdontograma || !selectedProc) return null;

  const valor = selectedValor || selectedProc.valor;
  const grupoNome = selectedCategoria?.nome ?? '';
  const catprocId = selectedCatprocId || 0;
  const exigeFaces = effectiveQtdFaces > 0;

  const getCurrentSelectionType = (): 'permanent' | 'deciduous' | 'none' => {
    if (selectedTeeth.length === 0) return 'none';
    const firstTooth = selectedTeeth[0];
    if (isDeciduousTooth(firstTooth)) return 'deciduous';
    if (isPermanentTooth(firstTooth)) return 'permanent';
    return 'none';
  };

  const handleToothClick = (id: number) => {
    if (selectedTeeth.includes(id)) {
      setSelectedTeeth(prev => prev.filter(t => t !== id));
      if (exigeFaces) {
        setFacesPorElemento(prev => {
          const next = { ...prev };
          delete next[String(id)];
          return next;
        });
      }
      return;
    }

    const clickedIsDeciduous = isDeciduousTooth(id);
    const currentType = getCurrentSelectionType();
    if (currentType !== 'none') {
      const currentIsDeciduous = currentType === 'deciduous';
      if (clickedIsDeciduous !== currentIsDeciduous) {
        setSelectedTeeth([]);
        setFacesPorElemento({});
      }
    }

    if (exigeFaces) {
      setPendingToothForFaces(id);
      setShowFacesModal(true);
    } else {
      setSelectedTeeth(prev => [...prev, id]);
    }
  };

  const handleSelectAll = () => {
    if (exigeFaces) {
      alert('Selecione dente por dente para escolher as faces');
      return;
    }
    const permanentes = [...odontogramaSuperior, ...odontogramaInferior].map(t => t.id);
    setSelectedTeeth(permanentes);
  };

  const handleSelectSuperior = () => {
    if (exigeFaces) {
      alert('Selecione dente por dente para escolher as faces');
      return;
    }
    setSelectedTeeth(odontogramaSuperior.map(t => t.id));
  };

  const handleSelectInferior = () => {
    if (exigeFaces) {
      alert('Selecione dente por dente para escolher as faces');
      return;
    }
    setSelectedTeeth(odontogramaInferior.map(t => t.id));
  };

  const handleSelectDeciduos = () => {
    if (exigeFaces) {
      alert('Selecione dente por dente para escolher as faces');
      return;
    }
    const deciduosTeeth = [...odontogramaDeciduoSuperior, ...odontogramaDeciduoInferior].map(t => t.id);
    setSelectedTeeth(deciduosTeeth);
  };

  const handleSelectDeciduosSuperior = () => {
    if (exigeFaces) {
      alert('Selecione dente por dente para escolher as faces');
      return;
    }
    setSelectedTeeth(odontogramaDeciduoSuperior.map(t => t.id));
  };

  const handleSelectDeciduosInferior = () => {
    if (exigeFaces) {
      alert('Selecione dente por dente para escolher as faces');
      return;
    }
    setSelectedTeeth(odontogramaDeciduoInferior.map(t => t.id));
  };

  const handleClear = () => {
    setSelectedTeeth([]);
    setFacesPorElemento({});
  };

  const handleClose = () => {
    setShowOdontograma(false);
    setSelectedTeeth([]);
    setFacesPorElemento({});
    setSelectedProc(null);
    setSelectedCategoria(null);
    setSelectedGrupo(null);
  };

  const allFacesComplete = () => {
    if (!exigeFaces) return true;
    return selectedTeeth.every(t => {
      const f = facesPorElemento[String(t)] || '';
      const count = f ? f.split(',').filter(x => x.trim()).length : 0;
      return count === effectiveQtdFaces;
    });
  };

  const handleConfirm = () => {
    if (selectedTeeth.length === 0) return;

    if (exigeFaces && !allFacesComplete()) {
      alert(`Selecione ${effectiveQtdFaces} face(s) em todos os dentes`);
      return;
    }

    selectedTeeth.forEach(tooth => {
      const faces = facesPorElemento[String(tooth)] || '';
      addItem({
        procId: selectedProc.id,
        procNome: selectedProc.nome,
        area: selectedArea,
        elemento: tooth,
        valor: valor,
        grupoId: 0,
        grupoNome: grupoNome,
        qtde: 1,
        faces: faces,
        catprocId: catprocId,
        gpitemId: 0,
        obsPersonalizado: '',
      });
    });

    handleClose();
  };

  const handleNaoSeAplica = () => {
    if (exigeFaces) {
      setPendingToothForFaces(0);
      setShowFacesModal(true);
    } else {
      addItem({
        procId: selectedProc.id,
        procNome: selectedProc.nome,
        area: selectedArea,
        elemento: 0,
        valor: valor,
        grupoId: 0,
        grupoNome: grupoNome,
        qtde: 1,
        faces: '',
        catprocId: catprocId,
        gpitemId: 0,
        obsPersonalizado: '',
      });
      handleClose();
    }
  };

  const totalPreview = selectedTeeth.length * valor;
  const confirmDisabled = selectedTeeth.length === 0 || (exigeFaces && !allFacesComplete());

  return (
    <Styled.ModalOverlay onClick={handleClose}>
      <Styled.ModalContent onClick={e => e.stopPropagation()}>
        <Styled.ModalHeader>
          <Styled.CloseButton onClick={handleClose}>
            <IoClose size={24} />
          </Styled.CloseButton>
          <h3>🦷 Selecione os Elementos</h3>
          <p>
            {selectedProc.nome}
            {grupoNome && ` (${grupoNome})`}
          </p>
          {showValues && (
            <p>R$ {formatMoeda(valor)} por elemento</p>
          )}
          {selectedTeeth.length > 0 && showValues && (
            <div className="total-preview">
              Total: R$ {formatMoeda(totalPreview)} ({selectedTeeth.length} elemento{selectedTeeth.length > 1 ? 's' : ''})
            </div>
          )}
          {exigeFaces && (
            <p style={{ color: '#fbbf24', fontSize: 12, marginTop: 4 }}>
              Faces obrigatórias: {effectiveQtdFaces} por elemento
            </p>
          )}
        </Styled.ModalHeader>

        <Styled.ModalBody>
          <Styled.QuickButtons>
            <Styled.QuickButton onClick={handleClear}>Limpar</Styled.QuickButton>
            <Styled.QuickButton onClick={handleSelectAll}>Todos</Styled.QuickButton>
            <Styled.QuickButton onClick={handleSelectSuperior}>Superior</Styled.QuickButton>
            <Styled.QuickButton onClick={handleSelectInferior}>Inferior</Styled.QuickButton>
          </Styled.QuickButtons>

          <Styled.OdontogramaContainer>
            <Styled.ArcadaLabel>Arcada Superior - Permanentes</Styled.ArcadaLabel>
            <Styled.ArcadaWrapper>
              <Styled.Arcada>
                {odontogramaSuperior.map((tooth, index) => (
                  <React.Fragment key={tooth.id}>
                    <Styled.ToothBox
                      selected={selectedTeeth.includes(tooth.id)}
                      onClick={() => handleToothClick(tooth.id)}
                    >
                      <tooth.img />
                      <span>{tooth.id}</span>
                      {exigeFaces && facesPorElemento[String(tooth.id)] && (
                        <span style={{ fontSize: 7, color: selectedTeeth.includes(tooth.id) ? '#bbf7d0' : '#9b59b6' }}>
                          {facesPorElemento[String(tooth.id)]}
                        </span>
                      )}
                    </Styled.ToothBox>
                    {index === 7 && <Styled.QuadrantSeparator />}
                  </React.Fragment>
                ))}
              </Styled.Arcada>
            </Styled.ArcadaWrapper>

            <Styled.Divisor />

            <Styled.ArcadaLabel>Arcada Inferior - Permanentes</Styled.ArcadaLabel>
            <Styled.ArcadaWrapper>
              <Styled.Arcada>
                {odontogramaInferior.map((tooth, index) => (
                  <React.Fragment key={tooth.id}>
                    <Styled.ToothBox
                      selected={selectedTeeth.includes(tooth.id)}
                      onClick={() => handleToothClick(tooth.id)}
                    >
                      <span>{tooth.id}</span>
                      <tooth.img />
                      {exigeFaces && facesPorElemento[String(tooth.id)] && (
                        <span style={{ fontSize: 7, color: selectedTeeth.includes(tooth.id) ? '#bbf7d0' : '#9b59b6' }}>
                          {facesPorElemento[String(tooth.id)]}
                        </span>
                      )}
                    </Styled.ToothBox>
                    {index === 7 && <Styled.QuadrantSeparator />}
                  </React.Fragment>
                ))}
              </Styled.Arcada>
            </Styled.ArcadaWrapper>

            <Styled.DeciduosSection>
              <Styled.DeciduosHeader onClick={() => setShowDeciduos(!showDeciduos)}>
                <span>🍼 Dentes Decíduos (de leite)</span>
                {showDeciduos ? <IoChevronUp size={20} /> : <IoChevronDown size={20} />}
              </Styled.DeciduosHeader>
              
              {showDeciduos && (
                <Styled.DeciduosContent>
                  <Styled.QuickButtons style={{ justifyContent: 'center', marginBottom: '12px' }}>
                    <Styled.QuickButton onClick={handleSelectDeciduos}>Todos Decíduos</Styled.QuickButton>
                    <Styled.QuickButton onClick={handleSelectDeciduosSuperior}>Superior</Styled.QuickButton>
                    <Styled.QuickButton onClick={handleSelectDeciduosInferior}>Inferior</Styled.QuickButton>
                  </Styled.QuickButtons>

                  <Styled.ArcadaLabel>Superior - Decíduos</Styled.ArcadaLabel>
                  <Styled.ArcadaWrapper>
                    <Styled.Arcada>
                      {odontogramaDeciduoSuperior.map((tooth, index) => (
                        <React.Fragment key={tooth.id}>
                          <Styled.ToothBox
                            selected={selectedTeeth.includes(tooth.id)}
                            onClick={() => handleToothClick(tooth.id)}
                            deciduo
                          >
                            <tooth.img />
                            <span>{tooth.id}</span>
                          </Styled.ToothBox>
                          {index === 4 && <Styled.QuadrantSeparator />}
                        </React.Fragment>
                      ))}
                    </Styled.Arcada>
                  </Styled.ArcadaWrapper>

                  <Styled.DeciduosDivisor />

                  <Styled.ArcadaLabel>Inferior - Decíduos</Styled.ArcadaLabel>
                  <Styled.ArcadaWrapper>
                    <Styled.Arcada>
                      {odontogramaDeciduoInferior.map((tooth, index) => (
                        <React.Fragment key={tooth.id}>
                          <Styled.ToothBox
                            selected={selectedTeeth.includes(tooth.id)}
                            onClick={() => handleToothClick(tooth.id)}
                            deciduo
                          >
                            <span>{tooth.id}</span>
                            <tooth.img />
                          </Styled.ToothBox>
                          {index === 4 && <Styled.QuadrantSeparator />}
                        </React.Fragment>
                      ))}
                    </Styled.Arcada>
                  </Styled.ArcadaWrapper>
                </Styled.DeciduosContent>
              )}
            </Styled.DeciduosSection>

            {selectedTeeth.length > 0 && (
              <Styled.SelectionCount>
                <span>Selecionados:</span>
                <span className="count">{selectedTeeth.length}</span>
              </Styled.SelectionCount>
            )}
          </Styled.OdontogramaContainer>
        </Styled.ModalBody>

        <Styled.ModalFooter>
          <Styled.FooterButton variant="text" onClick={handleNaoSeAplica}>
            N/A
          </Styled.FooterButton>
          <Styled.FooterButton variant="secondary" onClick={handleClose}>
            Cancelar
          </Styled.FooterButton>
          <Styled.FooterButton
            variant="primary"
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            Confirmar ({selectedTeeth.length})
          </Styled.FooterButton>
        </Styled.ModalFooter>
      </Styled.ModalContent>
    </Styled.ModalOverlay>
  );
};

export { BudgetOdontograma };
